import { useState, useEffect, useCallback, useRef } from 'react'
import { BrandsService, BrandsFilters } from '../services/api/brandsService'
import { Brand } from '../types/brand'
import { PaginatedResponse } from '../types/common'
import { logger } from '../utils/logger'

interface UseBrandsReturn {
  brands: PaginatedResponse<Brand> | null
  isLoading: boolean
  error: string | null
  filters: BrandsFilters
  setFilters: (updates: Partial<BrandsFilters>) => void
  resetFilters: () => void
  createBrand: (brand: Omit<Brand, 'uuid'>) => Promise<void>
  updateBrand: (brand: Brand) => Promise<void>
  deleteBrand: (uuid: string) => Promise<void>
  validateBrand: (uuid: string) => Promise<void>
  mergeBrand: (brandId: string, mergeWithId: string) => Promise<void>
  refetch: () => void
}

const defaultFilters: BrandsFilters = {
  page: 1,
  limit: 20,
  toValidate: null,
  search: ''
}

export const useBrands = (initialFilters?: Partial<BrandsFilters>): UseBrandsReturn => {
  const [brands, setBrands] = useState<PaginatedResponse<Brand> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFiltersState] = useState<BrandsFilters>({
    ...defaultFilters,
    ...initialFilters
  })
  const lastFetchFiltersRef = useRef<string | null>(null)

  const fetchBrands = useCallback(async () => {
    // Create a unique key for this filter combination
    const filtersKey = JSON.stringify(filters)
    
    // Skip if we just fetched with the same filters
    if (lastFetchFiltersRef.current === filtersKey) {
      return
    }
    
    lastFetchFiltersRef.current = filtersKey
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await BrandsService.getBrands(filters)
      setBrands(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch brands'
      logger.error('Error fetching brands:', err)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  const setFilters = useCallback((updates: Partial<BrandsFilters>) => {
    setFiltersState(prev => ({
      ...prev,
      ...updates,
      // Reset page to 1 when changing filters (except when changing page itself)
      page: updates.page !== undefined ? updates.page : (
        updates.search !== undefined || updates.toValidate !== undefined || updates.limit !== undefined ? 1 : prev.page
      )
    }))
  }, [])

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters)
  }, [])

  const createBrand = async (brand: Omit<Brand, 'uuid'>) => {
    try {
      await BrandsService.createBrand(brand)
      await fetchBrands()
    } catch (err) {
      logger.error('Error creating brand:', err)
      throw err
    }
  }

  const updateBrand = async (brand: Brand) => {
    try {
      await BrandsService.updateBrand(brand)
      await fetchBrands()
    } catch (err) {
      logger.error('Error updating brand:', err)
      throw err
    }
  }

  const deleteBrand = async (uuid: string) => {
    try {
      await BrandsService.deleteBrand(uuid)
      await fetchBrands()
    } catch (err) {
      logger.error('Error deleting brand:', err)
      throw err
    }
  }

  const validateBrand = async (uuid: string) => {
    try {
      await BrandsService.validateBrand(uuid)
      await fetchBrands()
    } catch (err) {
      logger.error('Error validating brand:', err)
      throw err
    }
  }

  const mergeBrand = async (brandId: string, mergeWithId: string) => {
    try {
      await BrandsService.mergeBrand(brandId, mergeWithId)
      await fetchBrands()
    } catch (err) {
      logger.error('Error merging brands:', err)
      throw err
    }
  }

  const refetch = useCallback(() => {
    // Force refetch by clearing the cache
    lastFetchFiltersRef.current = null
    fetchBrands()
  }, [fetchBrands])

  return {
    brands,
    isLoading,
    error,
    filters,
    setFilters,
    resetFilters,
    createBrand,
    updateBrand,
    deleteBrand,
    validateBrand,
    mergeBrand,
    refetch
  }
}